import Swiper from 'swiper'
import { SwiperOptions } from 'swiper/types/swiper-options'
import { Navigation, Pagination, Autoplay, EffectFade, Grid } from 'swiper/modules'

const createNavigation = (carousel: HTMLElement) => {
    const navigation: HTMLElement = document.createElement('nav')
    navigation.classList.add('swiper-navigation')
    carousel.appendChild(navigation)

    const buttonPrev: HTMLButtonElement = document.createElement('button')
    buttonPrev.classList.add('swiper-navigation__button')
    buttonPrev.classList.add('swiper-navigation__button-prev')
    buttonPrev.ariaLabel = `{{js.swiper.navigation.slide.previous.aria-label}}`

    const buttonNext: HTMLButtonElement = document.createElement('button')
    buttonNext.classList.add('swiper-navigation__button')
    buttonNext.classList.add('swiper-navigation__button-next')
    buttonNext.ariaLabel = `{{js.swiper.navigation.slide.next.aria-label}}`

    navigation.appendChild(buttonPrev)
    navigation.appendChild(buttonNext)

    carousel.appendChild(navigation)
}

const createPagination = (carousel: HTMLElement) => {
    const pagination: HTMLElement = document.createElement('nav')
    pagination.classList.add('swiper-pagination')
    carousel.appendChild(pagination)
}

const createCallToAction = (swiper: Swiper) => {
    const container = document.createElement('div')
    container.className = 'swiper-cta'

    const buttons = swiper.el.querySelectorAll<HTMLAnchorElement>('.swiper-slide.attrax-button')
    buttons.forEach((button) => {
        button.remove()
        button.classList.remove('swiper-slide')
        button.removeAttribute('style')
        button.removeAttribute('tabindex')
        button.href = button.href.toLowerCase()
        container.appendChild(button)
    })

    swiper.update()
    swiper.el.appendChild(container)
}

const createWrapper = (carousel: HTMLElement) => {
    const swiperWrapper = carousel.querySelector<HTMLDivElement>('.swiper-wrapper')
    if (!swiperWrapper) return
    const wrapper = document.createElement('div')
    wrapper.classList.add('swiper-wrapper-container')
    wrapper.appendChild(swiperWrapper)
    carousel.appendChild(wrapper)
}

const createCarousel = (query: string, swiperOptions: SwiperOptions) => {
    const carousels = document.querySelectorAll<HTMLElement>(query)
    carousels.forEach((carousel) => {
        carousel.classList.add('swiper')
        
        const children = [...carousel.children]
        children.forEach((child: HTMLElement) => {
            child.classList.add('swiper-slide')
        })

        if (swiperOptions.navigation !== false) {
            createNavigation(carousel)
            swiperOptions.navigation = {
                prevEl: '.swiper-navigation__button-prev',
                nextEl: '.swiper-navigation__button-next',
            }
        }

        if (swiperOptions.pagination !== false) {
            createPagination(carousel)
            swiperOptions.pagination = {
                el: '.swiper-pagination',
                clickable: true,
                bulletElement: 'button',
            }
        }

        const swiper = new Swiper(carousel, {
            modules: [ Navigation, Pagination, Autoplay, EffectFade, Grid ],
            createElements: true,
            watchOverflow: true,
            watchSlidesProgress: true,
            ...swiperOptions,
        })

        createWrapper(carousel)
    })
}

const createCarouselReact = (ref: React.RefObject<HTMLDivElement>, swiperOptions: SwiperOptions) => {
    const carousel = ref.current
    if (!carousel) return

    carousel.classList.add('swiper')

    const children = [...carousel.children]
    children.forEach((child: HTMLElement) => {
        child.classList.add('swiper-slide')
    })

    if (swiperOptions.navigation !== false) {
        createNavigation(carousel)
        swiperOptions.navigation = {
            prevEl: '.swiper-navigation__button-prev',
            nextEl: '.swiper-navigation__button-next',
        }
    }

    if (swiperOptions.pagination !== false) {
        createPagination(carousel)
        swiperOptions.pagination = {
            el: '.swiper-pagination',
            clickable: true,
            bulletElement: 'button',
        }
    }

    const swiper = new Swiper(carousel, {
        modules: [ Navigation, Pagination, Autoplay, EffectFade, Grid ],
        createElements: true,
        watchOverflow: true,
        watchSlidesProgress: true,
        ...swiperOptions,
    })

    createWrapper(carousel)
}

const createJobsCarousel = () => {
    createCarousel('.carousel-jobs .attrax-list-widget__list--has-items', {
        slidesPerView: 1,
        spaceBetween: 30,
        breakpoints: {
            768: {
                slidesPerView: 2,
            },
            1200: {
                slidesPerView: 3,
            },
        },
    })
}

const createPostsCarousel = () => {
    createCarousel('.carousel-posts .attrax-list-widget__list--has-items', {
        slidesPerView: 1,
        spaceBetween: 30,
        breakpoints: {
            768: {
                slidesPerView: 2,
            },
            1200: {
                slidesPerView: 3,
            },
        },
    })
}

export const createGeneralCarousel = (ref) => {
    createCarouselReact(ref, {
        slidesPerView: 1,
        spaceBetween: 20,
        breakpoints: {
            666: {
                slidesPerView: 1,
            },
            991: {
                slidesPerView: 2,
            },
            1200: {
                slidesPerView: 3,
            },
        },
    })
}

export const createPvpCarousel = (ref) => {
    let isAutoplaying = false
    let autoplayTimeout = 5000

    const nextSlide = (swiper) => {
        setInterval(() => {
            if (!isAutoplaying) return
            swiper.isEnd ? swiper.slideTo(0) : swiper.slideNext()
        }, autoplayTimeout)
    }

    createCarouselReact(ref, {
        slidesPerView: 1,
        grabCursor: false,
        allowTouchMove: false,
        on: {
            init: (swiper) => {
                isAutoplaying = true
                nextSlide(swiper)

                swiper.el.addEventListener('mouseenter', () => {
                    isAutoplaying = false
                })

                swiper.el.addEventListener('mouseleave', () => {
                    isAutoplaying = true
                })
            },
            resize: (swiper) => {
                if (swiper.width < 991) {
                    isAutoplaying = false
                } else {
                    isAutoplaying = true
                }
            }
        },
    })
}

const createCallToActionCarousel = () => {
    createCarousel('.banner-call-to-action', {
        navigation: false,
        pagination: false,
        loop: true,
        grabCursor: true,
        effect: 'fade',
        fadeEffect: {
            crossFade: true,
        },
        autoplay: {
            delay: 5000,
            pauseOnMouseEnter: true,
        },
    })
}

const createTestimonialsCarousel = () => {
    createCarousel('.banner-testimonials', {
        slidesPerView: 1,
        spaceBetween: 40,
        navigation: true,
        pagination: true,
        loop: true,
        grabCursor: true,
        autoplay: {
            delay: 5000,
            pauseOnMouseEnter: true,
        },
    })
}

export const initialise = () => {
    window.addEventListener('DOMContentLoaded', () => {
        createJobsCarousel()
        createPostsCarousel()
        createTestimonialsCarousel()
        createCallToActionCarousel()
    })
}